// src/components/vcard/ExtremeKitInfo.tsx
import React, { useState, useEffect, useRef } from "react";
import {
  AlertCircle,
  Plus,
  Trash2,
  Loader2,
  CheckCircle2,
  ShieldPlus,
  ShieldAlert,
} from "lucide-react";
import { useVCardProfileSync } from "../../hooks/useVCardProfileSync";
import type { VCard } from "../../types/vcard";
import OwnerSection from "./shared/OwnerSection";
import useUniversalToggleWithAddress from "../../hooks/useUniversalToggleWithAddress";
import {
  CATEGORY_SPECIFIC_FIELDS,
  getCategorySyncFields,
} from "../../constants/ownerFields";
import { DEFAULT_COUNTRY_CODE } from "../../constants/countryCodes";
import {
  formatPhoneBodyForDisplay,
  normalizePhoneForStorage,
  stripCountryCodeForDisplay,
} from "../../utils/phone";
import { useUserStore } from "../../store/userStore";
import type { UserProfile } from "../../types/user";
import PremiumFeatureNotice from "./shared/PremiumFeatureNotice";
import CountryCodeSelect from "../ui/CountryCodeSelect";

interface ExtremeKitEquipment {
  id: string;
  name: string;
  description?: string;
  serialNumber?: string;
}

interface ExtremeKitInfoProps {
  formData: Partial<VCard>;
  handleChange: (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => void;
  handleSubmit: (e: React.FormEvent) => Promise<void>;
  isSubmitting?: boolean;
  showSaveConfirmation?: boolean;
  onToggle: () => void;
  isActive: boolean;
}

const MAX_FREE_EQUIPMENT = 3;
const BLOOD_TYPES = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];

const getMonthExpiryNotice = (
  monthValue: string,
): { text: string; isWarning: boolean } => {
  if (!monthValue) {
    return { text: "", isWarning: false };
  }

  const [yearText, monthText] = monthValue.split("-");
  const year = Number(yearText);
  const month = Number(monthText);
  if (!Number.isFinite(year) || !Number.isFinite(month)) {
    return { text: "", isWarning: false };
  }

  const now = new Date();
  const currentIndex = now.getFullYear() * 12 + now.getMonth();
  const targetIndex = year * 12 + (month - 1);
  const diffMonths = targetIndex - currentIndex;

  if (diffMonths < 0) {
    const absoluteMonths = Math.abs(diffMonths);
    return {
      text: `Expired ${absoluteMonths} month${absoluteMonths === 1 ? "" : "s"} ago`,
      isWarning: true,
    };
  }

  if (diffMonths === 0) {
    return { text: "Expires this month", isWarning: true };
  }

  return {
    text: `Expires in ${diffMonths} month${diffMonths === 1 ? "" : "s"}`,
    isWarning: diffMonths <= 1,
  };
};

const getDateExpiryNotice = (
  dateValue: string,
): { text: string; isWarning: boolean } => {
  if (!dateValue) {
    return { text: "", isWarning: false };
  }

  const expiryDate = new Date(`${dateValue}T00:00:00`);
  if (Number.isNaN(expiryDate.getTime())) {
    return { text: "", isWarning: false };
  }

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffMs = expiryDate.getTime() - today.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    const absoluteDays = Math.abs(diffDays);
    return {
      text: `Expired ${absoluteDays} day${absoluteDays === 1 ? "" : "s"} ago`,
      isWarning: true,
    };
  }

  if (diffDays === 0) {
    return { text: "Expires today", isWarning: true };
  }

  return {
    text: `Expires in ${diffDays} day${diffDays === 1 ? "" : "s"}`,
    isWarning: diffDays <= 30,
  };
};

const ExtremeKitInfo: React.FC<ExtremeKitInfoProps> = ({
  formData,
  handleChange,
  handleSubmit,
  isSubmitting = false,
  showSaveConfirmation = false,
  onToggle,
  isActive,
}) => {
  void onToggle;
  void isActive;

  const [equipment, setEquipment] = useState<ExtremeKitEquipment[]>(
    formData.categorySpecificInfo?.extremeKit?.equipment || [
      {
        id: "1",
        name: "",
        description: "",
        serialNumber: "",
      },
    ],
  );
  const [showPremiumNotice, setShowPremiumNotice] = useState(false);
  const [localShowSaveConfirmation, setLocalShowSaveConfirmation] =
    useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mobilePhoneCountryCode, setMobilePhoneCountryCode] =
    useState(DEFAULT_COUNTRY_CODE);
  const [workPhoneCountryCode, setWorkPhoneCountryCode] =
    useState(DEFAULT_COUNTRY_CODE);
  const [emergencyPhoneCountryCode, setEmergencyPhoneCountryCode] =
    useState(DEFAULT_COUNTRY_CODE);
  const profile = useUserStore((state) => state.profile);
  const updateProfile = useUserStore((state) => state.updateProfile);

  // Update the syncFields to target the nested extremeKit field.
  const { vcard: extremeKitVCard } = useVCardProfileSync("extremeKit", {
    syncFields: getCategorySyncFields("extremeKit"),
    debounceMs: 300,
  });

  const {
    universalToggleWithAddress,
    isLoading: toggleLoading,
    error: toggleError,
  } = useUniversalToggleWithAddress();

  const handleToggle = async () => {
    try {
      await universalToggleWithAddress(extremeKitVCard?.id || "extremeKit");
    } catch (err) {
      console.error(err);
      setError("Toggle failed");
    }
  };

  // Use a ref to avoid update loops when syncing equipment.
  const isUpdatingEquipment = useRef(false);

  // Update form data when equipment changes
  useEffect(() => {
    if (isUpdatingEquipment.current) return;

    const currentEquipmentJson = JSON.stringify(equipment);
    const formDataEquipmentJson = JSON.stringify(
      formData.categorySpecificInfo?.extremeKit?.equipment,
    );

    if (currentEquipmentJson !== formDataEquipmentJson) {
      isUpdatingEquipment.current = true;
      handleChange({
        target: {
          name: CATEGORY_SPECIFIC_FIELDS.EXTREME_KIT_EQUIPMENT,
          value: equipment,
        },
      } as any);
      setTimeout(() => {
        isUpdatingEquipment.current = false;
      }, 0);
    }
  }, [
    equipment,
    handleChange,
    formData.categorySpecificInfo?.extremeKit?.equipment,
  ]);

  const vcardEquipment =
    extremeKitVCard?.categorySpecificInfo?.extremeKit?.equipment;

  // Keep local equipment in sync with vCard updates without bouncing local edits.
  useEffect(() => {
    if (
      vcardEquipment &&
      Array.isArray(vcardEquipment) &&
      vcardEquipment.length > 0
    ) {
      setEquipment((currentEquipment) =>
        JSON.stringify(vcardEquipment) === JSON.stringify(currentEquipment)
          ? currentEquipment
          : vcardEquipment,
      );
    }
  }, [vcardEquipment]);

  const handleEquipmentChange = (
    id: string,
    field: keyof ExtremeKitEquipment,
    value: string,
  ) => {
    const updatedEquipment = equipment.map((item) =>
      item.id === id ? { ...item, [field]: value } : item,
    );
    setEquipment(updatedEquipment);
  };

  const addEquipment = () => {
    if (equipment.length >= MAX_FREE_EQUIPMENT) {
      setShowPremiumNotice(true);
      return;
    }
    setEquipment([
      ...equipment,
      {
        id: Date.now().toString(),
        name: "",
        description: "",
        serialNumber: "",
      },
    ]);
  };

  const removeEquipment = (id: string) => {
    const newEquipment = equipment.filter((item) => item.id !== id);
    setEquipment(newEquipment);
  };

  const syncedFormData = {
    ...formData,
    firstName: extremeKitVCard?.firstName || formData.firstName,
    lastName: extremeKitVCard?.lastName || formData.lastName,
    email:
      extremeKitVCard?.email ||
      extremeKitVCard?.personalEmail ||
      formData.email ||
      formData.personalEmail,
    workEmail: extremeKitVCard?.workEmail || formData.workEmail,
    mobilePhone: extremeKitVCard?.mobilePhone || formData.mobilePhone,
    mobilePhoneCountryCode:
      extremeKitVCard?.mobilePhoneCountryCode || mobilePhoneCountryCode,
    workPhone: extremeKitVCard?.workPhone || formData.workPhone,
    workPhoneCountryCode:
      extremeKitVCard?.workPhoneCountryCode || workPhoneCountryCode,
    isActive: extremeKitVCard?.isActive || formData.isActive || false,
    categorySpecificInfo: {
      ...formData.categorySpecificInfo,
      extremeKit: {
        ...formData.categorySpecificInfo?.extremeKit,
        ...extremeKitVCard?.categorySpecificInfo?.extremeKit,
        equipment: equipment,
        emergencyContact: {
          ...formData.categorySpecificInfo?.extremeKit?.emergencyContact,
          ...extremeKitVCard?.categorySpecificInfo?.extremeKit
            ?.emergencyContact,
          phoneCountryCode: emergencyPhoneCountryCode,
        },
        medicalInfo: {
          ...formData.categorySpecificInfo?.extremeKit?.medicalInfo,
          ...extremeKitVCard?.categorySpecificInfo?.extremeKit?.medicalInfo,
        },
      },
    },
  };
  const medicareExpiry =
    syncedFormData.categorySpecificInfo?.extremeKit?.medicalInfo
      ?.medicareExpiry || "";
  const concessionNumberExpiry =
    syncedFormData.categorySpecificInfo?.extremeKit?.medicalInfo
      ?.concessionNumberExpiry || "";
  const medicareExpiryNotice = getMonthExpiryNotice(medicareExpiry);
  const concessionExpiryNotice = getDateExpiryNotice(concessionNumberExpiry);

  const handleEnhancedSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const formDataSnapshot = new FormData(e.currentTarget as HTMLFormElement);
    setError(null);
    try {
      await handleSubmit(e);
      if (extremeKitVCard) {
        setLocalShowSaveConfirmation(true);
        setTimeout(() => setLocalShowSaveConfirmation(false), 3000);
      }

      if (profile) {
        const profileUpdates: Partial<UserProfile> = {};
        const formFirstName =
          (formDataSnapshot.get("firstName") as string) || "";
        const formLastName = (formDataSnapshot.get("lastName") as string) || "";
        const formEmail = (formDataSnapshot.get("email") as string) || "";
        const formWorkEmail =
          (formDataSnapshot.get("workEmail") as string) || "";
        const formMobilePhone =
          (formDataSnapshot.get("mobilePhone") as string) || "";
        const formWorkPhone =
          (formDataSnapshot.get("workPhone") as string) || "";
        const formTravelPhone =
          (formDataSnapshot.get("travelPhone") as string) || "";
        const formEmergencyName =
          (formDataSnapshot.get(
            "categorySpecificInfo.extremeKit.emergencyContact.name",
          ) as string) || "";
        const formEmergencyPhoneRaw =
          (formDataSnapshot.get(
            "categorySpecificInfo.extremeKit.emergencyContact.phone",
          ) as string) || "";
        const formEmergencyRelationship =
          (formDataSnapshot.get(
            "categorySpecificInfo.extremeKit.emergencyContact.relationship",
          ) as string) || "";
        const formEmergencyPhone = formEmergencyPhoneRaw
          ? formEmergencyPhoneRaw.startsWith("+")
            ? formEmergencyPhoneRaw
            : `${emergencyPhoneCountryCode}${formEmergencyPhoneRaw.replace(/^0+/, "")}`
          : "";
        const formBloodType =
          (formDataSnapshot.get(
            "categorySpecificInfo.extremeKit.medicalInfo.bloodType",
          ) as string) || "";
        const formAllergies =
          (formDataSnapshot.get(
            "categorySpecificInfo.extremeKit.medicalInfo.allergies",
          ) as string) || "";
        const formConditions =
          (formDataSnapshot.get(
            "categorySpecificInfo.extremeKit.medicalInfo.conditions",
          ) as string) || "";
        const formMedications =
          (formDataSnapshot.get(
            "categorySpecificInfo.extremeKit.medicalInfo.medications",
          ) as string) || "";
        const formHealthFund =
          (formDataSnapshot.get(
            "categorySpecificInfo.extremeKit.medicalInfo.healthFund",
          ) as string) || "";
        const formMemberNumber =
          (formDataSnapshot.get(
            "categorySpecificInfo.extremeKit.medicalInfo.memberNumber",
          ) as string) || "";
        const formHealthFundId =
          (formDataSnapshot.get(
            "categorySpecificInfo.extremeKit.medicalInfo.healthFundId",
          ) as string) || "";
        const formCardNumber =
          (formDataSnapshot.get(
            "categorySpecificInfo.extremeKit.medicalInfo.cardNumber",
          ) as string) || "";
        const formMedicareNumber =
          (formDataSnapshot.get(
            "categorySpecificInfo.extremeKit.medicalInfo.medicareNumber",
          ) as string) || "";
        const formMedicareIrn =
          (formDataSnapshot.get(
            "categorySpecificInfo.extremeKit.medicalInfo.medicareIrn",
          ) as string) || "";
        const formMedicareExpiry =
          (formDataSnapshot.get(
            "categorySpecificInfo.extremeKit.medicalInfo.medicareExpiry",
          ) as string) || "";
        const formConcessionNumberExpiry =
          (formDataSnapshot.get(
            "categorySpecificInfo.extremeKit.medicalInfo.concessionNumberExpiry",
          ) as string) || "";
        const formNdisNumber =
          (formDataSnapshot.get(
            "categorySpecificInfo.extremeKit.medicalInfo.ndisNumber",
          ) as string) || "";

        if (formFirstName && formFirstName !== profile.firstName) {
          profileUpdates.firstName = formFirstName;
        }

        if (formLastName && formLastName !== profile.lastName) {
          profileUpdates.lastName = formLastName;
        }

        if (formEmail && formEmail !== profile.email) {
          profileUpdates.email = formEmail;
        }

        if (formWorkEmail !== (profile.alternativeEmail || "")) {
          profileUpdates.alternativeEmail = formWorkEmail;
        }

        if (formMobilePhone && formMobilePhone !== profile.phone) {
          profileUpdates.phone = formMobilePhone;
        }

        if (formWorkPhone !== (profile.workPhone || "")) {
          profileUpdates.workPhone = formWorkPhone;
        }

        if (formTravelPhone !== (profile.travelPhone || "")) {
          profileUpdates.travelPhone = formTravelPhone;
        }

        const emergencyChanged =
          formEmergencyName !== (profile.emergencyContact?.name || "") ||
          formEmergencyPhone !== (profile.emergencyContact?.phone || "") ||
          formEmergencyRelationship !==
            (profile.emergencyContact?.relationship || "");

        if (emergencyChanged) {
          profileUpdates.emergencyContact = {
            name: formEmergencyName,
            phone: formEmergencyPhone,
            relationship: formEmergencyRelationship,
          };
        }

        const medicalChanged =
          formBloodType !== (profile.medicalInfo?.bloodType || "") ||
          formAllergies !== (profile.medicalInfo?.allergies || "") ||
          formConditions !== (profile.medicalInfo?.conditions || "") ||
          formMedications !== (profile.medicalInfo?.medications || "") ||
          formHealthFund !== (profile.medicalInfo?.healthFund || "") ||
          formMemberNumber !== (profile.medicalInfo?.memberNumber || "") ||
          formHealthFundId !== (profile.medicalInfo?.healthFundId || "") ||
          formCardNumber !== (profile.medicalInfo?.cardNumber || "") ||
          formMedicareNumber !== (profile.medicalInfo?.medicareNumber || "") ||
          formMedicareIrn !== (profile.medicalInfo?.medicareIrn || "") ||
          formMedicareExpiry !== (profile.medicalInfo?.medicareExpiry || "") ||
          formConcessionNumberExpiry !==
            (profile.medicalInfo?.concessionNumberExpiry || "") ||
          formNdisNumber !== (profile.medicalInfo?.ndisNumber || "");

        if (medicalChanged) {
          profileUpdates.medicalInfo = {
            bloodType: formBloodType,
            allergies: formAllergies,
            conditions: formConditions,
            medications: formMedications,
            healthFund: formHealthFund,
            memberNumber: formMemberNumber,
            healthFundId: formHealthFundId,
            cardNumber: formCardNumber,
            medicareNumber: formMedicareNumber,
            medicareIrn: formMedicareIrn,
            medicareExpiry: formMedicareExpiry,
            concessionNumberExpiry: formConcessionNumberExpiry,
            ndisNumber: formNdisNumber,
          };
        }

        if (Object.keys(profileUpdates).length > 0) {
          await updateProfile(profileUpdates);
        }
      }
    } catch (err) {
      console.error("Failed to sync data:", err);
      setError("Failed to save changes. Please try again.");
    }
  };

  return (
    <form onSubmit={handleEnhancedSubmit} className="space-y-6">
      {error && (
        <div className="flex items-center space-x-2 p-4 bg-red-50 text-red-700 rounded-lg">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {(showSaveConfirmation || localShowSaveConfirmation) && (
        <div className="flex items-center space-x-2 p-4 bg-green-50 text-green-700 rounded-lg">
          <CheckCircle2 className="h-5 w-5" />
          <span>Changes saved successfully!</span>
        </div>
      )}

      {/* Active/Inactive Toggle Section */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Extreme Kit Tag
          </h2>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            (Defaults to Home when not Active)
          </span>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            className="sr-only peer"
            checked={syncedFormData.isActive}
            onChange={handleToggle}
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600" />
          <span className="ml-2 text-sm font-medium text-gray-900">
            {syncedFormData.isActive ? (
              <span className="text-green-600">Active</span>
            ) : (
              <span className="text-orange-500">Inactive</span>
            )}
          </span>
        </label>
      </div>

      {/* Owner Information Section */}
      <OwnerSection
        formData={syncedFormData}
        handleChange={handleChange}
        mobilePhoneCountryCode={mobilePhoneCountryCode}
        setMobilePhoneCountryCode={setMobilePhoneCountryCode}
        workPhoneCountryCode={workPhoneCountryCode}
        setWorkPhoneCountryCode={setWorkPhoneCountryCode}
      />

      {/* Equipment List Section */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
          Equipment List
        </h3>
        <div className="space-y-4">
          {equipment.map((item) => (
            <div
              key={item.id}
              className="p-4 bg-gray-50 dark:bg-transparent rounded-lg"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Equipment Name*
                  </label>
                  <input
                    type="text"
                    value={item.name}
                    onChange={(e) =>
                      handleEquipmentChange(item.id, "name", e.target.value)
                    }
                    placeholder="e.g. White Water Raft (NRS), Parachute (PD), Kite Surfing Rig (Cabrinha)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    required
                  />
                </div>
                {equipment.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeEquipment(item.id)}
                    className="ml-2 p-2 text-gray-400 hover:text-red-500"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                )}
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Serial Number
                </label>
                <input
                  type="text"
                  value={item.serialNumber || ""}
                  onChange={(e) =>
                    handleEquipmentChange(
                      item.id,
                      "serialNumber",
                      e.target.value,
                    )
                  }
                  placeholder="e.g. WWR-2025 or CHUTE-007"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  value={item.description || ""}
                  onChange={(e) =>
                    handleEquipmentChange(
                      item.id,
                      "description",
                      e.target.value,
                    )
                  }
                  rows={2}
                  placeholder="e.g. Inflatable raft designed for Class IV–V rapids; or high-altitude skydiving canopy with reserve chute..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
              </div>
            </div>
          ))}

          {showPremiumNotice ? (
            <PremiumFeatureNotice
              message={`Adding more than ${MAX_FREE_EQUIPMENT} equipment items requires a premium subscription.`}
              onDismiss={() => setShowPremiumNotice(false)}
            />
          ) : (
            <button
              type="button"
              onClick={addEquipment}
              className="inline-flex items-center space-x-2 text-sm text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300"
            >
              <Plus className="h-4 w-4" />
              <span>Add Equipment Item</span>
            </button>
          )}
        </div>
      </div>

      {/* Emergency Contact Section */}
      <div>
        <h3 className="mb-4 flex items-center gap-2 text-lg font-medium text-gray-900 dark:text-gray-100">
          <ShieldAlert className="h-5 w-5 text-red-600" />
          <span>Emergency Contact</span>
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Contact Name*
            </label>
            <input
              type="text"
              name="categorySpecificInfo.extremeKit.emergencyContact.name"
              value={
                syncedFormData.categorySpecificInfo?.extremeKit
                  ?.emergencyContact?.name || ""
              }
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Contact Phone*
            </label>
            <div className="flex space-x-2">
              <CountryCodeSelect
                value={emergencyPhoneCountryCode}
                onChange={setEmergencyPhoneCountryCode}
                className="w-24"
                buttonClassName="h-[41px]"
              />
              <input
                type="tel"
                name="categorySpecificInfo.extremeKit.emergencyContact.phone"
                value={formatPhoneBodyForDisplay(
                  stripCountryCodeForDisplay(
                    syncedFormData.categorySpecificInfo?.extremeKit
                      ?.emergencyContact?.phone,
                    emergencyPhoneCountryCode,
                  ),
                )}
                onChange={(e) => {
                  handleChange({
                    target: {
                      name: "categorySpecificInfo.extremeKit.emergencyContact.phone",
                      value: normalizePhoneForStorage(
                        e.target.value,
                        emergencyPhoneCountryCode,
                      ),
                    },
                  } as React.ChangeEvent<HTMLInputElement>);
                }}
                required
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Relationship*
            </label>
            <input
              type="text"
              name="categorySpecificInfo.extremeKit.emergencyContact.relationship"
              value={
                syncedFormData.categorySpecificInfo?.extremeKit
                  ?.emergencyContact?.relationship || ""
              }
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />
          </div>
        </div>
      </div>

      {/* Medical Information Section */}
      <div>
        <h3 className="mb-4 flex items-center gap-2 text-lg font-medium text-gray-900 dark:text-gray-100">
          <ShieldPlus className="h-5 w-5 text-red-600" />
          <span>Medical Information</span>
        </h3>
        <div className="mb-4">
          <PremiumFeatureNotice
            title="Why This Matters"
            message="This information is stored on your SmartTag to help first responders in an emergency if you are unconscious or unable to communicate. Include critical details such as severe allergies (for example bee stings or penicillin), blood type, and medications such as blood thinners."
            showUpgrade={false}
            variant="medical"
          />
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Blood Type
            </label>
            <select
              name="categorySpecificInfo.extremeKit.medicalInfo.bloodType"
              value={
                syncedFormData.categorySpecificInfo?.extremeKit?.medicalInfo
                  ?.bloodType || ""
              }
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
            >
              <option value="">Select blood type</option>
              {BLOOD_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Allergies
            </label>
            <textarea
              name="categorySpecificInfo.extremeKit.medicalInfo.allergies"
              value={
                syncedFormData.categorySpecificInfo?.extremeKit?.medicalInfo
                  ?.allergies || ""
              }
              onChange={handleChange}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              placeholder="List any allergies"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Medical Conditions
            </label>
            <textarea
              name="categorySpecificInfo.extremeKit.medicalInfo.conditions"
              value={
                syncedFormData.categorySpecificInfo?.extremeKit?.medicalInfo
                  ?.conditions || ""
              }
              onChange={handleChange}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              placeholder="List any medical conditions"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Current Medications
            </label>
            <textarea
              name="categorySpecificInfo.extremeKit.medicalInfo.medications"
              value={
                syncedFormData.categorySpecificInfo?.extremeKit?.medicalInfo
                  ?.medications || ""
              }
              onChange={handleChange}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              placeholder="List any current medications"
            />
          </div>
          <div className="rounded-lg border border-sky-200 bg-sky-50/50 p-4 dark:border-sky-800 dark:bg-sky-950/30">
            <h4 className="mb-3 text-base font-semibold text-sky-800 dark:text-sky-200">
              Health Fund &amp; Benefits
            </h4>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Health Fund
                </label>
                <input
                  type="text"
                  name="categorySpecificInfo.extremeKit.medicalInfo.healthFund"
                  value={
                    syncedFormData.categorySpecificInfo?.extremeKit?.medicalInfo
                      ?.healthFund || ""
                  }
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  placeholder="Fund name"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Medicare No.
                  </label>
                  <input
                    type="text"
                    name="categorySpecificInfo.extremeKit.medicalInfo.medicareNumber"
                    value={
                      syncedFormData.categorySpecificInfo?.extremeKit
                        ?.medicalInfo?.medicareNumber || ""
                    }
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    placeholder="1215-45454-4"
                    inputMode="numeric"
                    pattern="[0-9 -]+"
                    title="Use numbers, spaces, or hyphens only."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    IRN
                  </label>
                  <input
                    type="text"
                    name="categorySpecificInfo.extremeKit.medicalInfo.medicareIrn"
                    value={
                      syncedFormData.categorySpecificInfo?.extremeKit
                        ?.medicalInfo?.medicareIrn || ""
                    }
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    placeholder="1"
                    inputMode="numeric"
                    maxLength={2}
                    pattern="[0-9]{1,2}"
                    title="Use 1 or 2 numbers."
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Member No.
                  </label>
                  <input
                    type="text"
                    name="categorySpecificInfo.extremeKit.medicalInfo.memberNumber"
                    value={
                      syncedFormData.categorySpecificInfo?.extremeKit
                        ?.medicalInfo?.memberNumber || ""
                    }
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    placeholder="Up to 10 digits"
                    inputMode="numeric"
                    maxLength={10}
                    pattern="[0-9]{1,10}"
                    title="Use up to 10 numbers."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    ID
                  </label>
                  <input
                    type="text"
                    name="categorySpecificInfo.extremeKit.medicalInfo.healthFundId"
                    value={
                      syncedFormData.categorySpecificInfo?.extremeKit
                        ?.medicalInfo?.healthFundId || ""
                    }
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    placeholder="2 chars"
                    maxLength={2}
                    pattern="[A-Za-z0-9]{1,2}"
                    title="Use up to 2 letters or numbers."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Card No.
                  </label>
                  <input
                    type="text"
                    name="categorySpecificInfo.extremeKit.medicalInfo.cardNumber"
                    value={
                      syncedFormData.categorySpecificInfo?.extremeKit
                        ?.medicalInfo?.cardNumber || ""
                    }
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    placeholder="00 to 99"
                    inputMode="numeric"
                    maxLength={2}
                    pattern="[0-9]{2}"
                    title="Use 2 numbers from 00 to 99."
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Medicare Exp.
                </label>
                <input
                  type="month"
                  name="categorySpecificInfo.extremeKit.medicalInfo.medicareExpiry"
                  value={medicareExpiry}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
                {medicareExpiryNotice.text && (
                  <p
                    className={`mt-1 text-sm ${medicareExpiryNotice.isWarning ? "text-amber-700 dark:text-amber-300" : "text-gray-600 dark:text-gray-400"}`}
                  >
                    ({medicareExpiryNotice.text})
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Concession No. Exp
                </label>
                <input
                  type="date"
                  name="categorySpecificInfo.extremeKit.medicalInfo.concessionNumberExpiry"
                  value={concessionNumberExpiry}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
                {concessionExpiryNotice.text && (
                  <p
                    className={`mt-1 text-sm ${concessionExpiryNotice.isWarning ? "text-amber-700 dark:text-amber-300" : "text-gray-600 dark:text-gray-400"}`}
                  >
                    ({concessionExpiryNotice.text})
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  NDIS Number
                </label>
                <input
                  type="text"
                  name="categorySpecificInfo.extremeKit.medicalInfo.ndisNumber"
                  value={
                    syncedFormData.categorySpecificInfo?.extremeKit?.medicalInfo
                      ?.ndisNumber || ""
                  }
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  placeholder="Up to 999999999"
                  inputMode="numeric"
                  maxLength={9}
                  pattern="[0-9]{1,9}"
                  title="Use up to 9 numbers."
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Submit Button Section */}
      <button
        type="submit"
        disabled={isSubmitting || toggleLoading}
        className="w-full bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors disabled:bg-red-400 disabled:cursor-not-allowed"
      >
        {isSubmitting || toggleLoading ? (
          <div className="flex items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            <span>Saving...</span>
          </div>
        ) : (
          "Save Extreme Kit Details"
        )}
      </button>

      {toggleError && (
        <p className="mt-4 text-red-600">Toggle Error: {toggleError}</p>
      )}
    </form>
  );
};

export default ExtremeKitInfo;
